import { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const STORAGE_KEY = 'diskarte_saved_searches';

const SavedSearches = ({ currentFilters, onLoadSearch }) => {
  const [savedSearches, setSavedSearches] = useState([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    loadSavedSearches();
  }, []);

  const loadSavedSearches = () => {
    try {
      const saved = localStorage?.getItem(STORAGE_KEY);
      if (saved) {
        setSavedSearches(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading saved searches:', error);
    }
  };

  const saveSearch = () => {
    if (!searchName?.trim()) return;

    const newSearch = {
      id: Date.now(),
      name: searchName?.trim(),
      filters: currentFilters,
      createdAt: new Date()?.toISOString()
    };

    const updated = [...savedSearches, newSearch];
    setSavedSearches(updated);
    localStorage?.setItem(STORAGE_KEY, JSON.stringify(updated));
    setSearchName('');
    setShowSaveDialog(false);
  };

  const deleteSearch = (id) => {
    const updated = savedSearches?.filter(s => s?.id !== id);
    setSavedSearches(updated);
    localStorage?.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  const loadSearch = (search) => {
    onLoadSearch?.(search?.filters);
  };

  const getFilterSummary = (filters) => {
    const parts = [];
    if (filters?.category && filters?.category !== 'all') parts?.push(filters?.category);
    if (filters?.location && filters?.location !== 'all') parts?.push(filters?.location);
    if (filters?.selectedTags?.length > 0) parts?.push(`${filters?.selectedTags?.length} tags`);
    if (filters?.searchQuery) parts?.push(`"${filters?.searchQuery}"`);
    return parts?.length > 0 ? parts?.join(' • ') : 'No filters';
  };

  if (savedSearches?.length === 0 && !showSaveDialog) {
    return (
      <div className="bg-muted/30 border border-border rounded-md p-4">
        <div className="flex items-start gap-3">
          <Icon name="Bookmark" size={20} className="text-muted-foreground flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-foreground mb-1">Save Your Searches</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Save your current filters to quickly access them later
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSaveDialog(true)}
              iconName="Plus"
              iconPosition="left"
              className="text-xs"
            >
              Save Current Search
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-md p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
          <Icon name="Bookmark" size={16} color="var(--color-primary)" />
          Saved Searches ({savedSearches?.length})
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowSaveDialog(true)}
          iconName="Plus"
        >
          Save
        </Button>
      </div>

      {showSaveDialog && (
        <div className="mb-4 p-3 bg-muted/50 border border-border rounded-md">
          <label className="text-xs font-medium text-foreground mb-2 block">
            Search Name
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={searchName}
              onChange={(e) => setSearchName(e?.target?.value)}
              onKeyDown={(e) => e?.key === 'Enter' && saveSearch()}
              placeholder="e.g., Plumbing jobs in Manila"
              className="flex-1 h-9 px-3 border border-border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              autoFocus
            />
            <Button
              variant="default"
              size="sm"
              onClick={saveSearch}
              disabled={!searchName?.trim()}
            >
              Save
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowSaveDialog(false);
                setSearchName('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {savedSearches?.slice(0, isExpanded ? undefined : 3)?.map((search) => (
          <div
            key={search?.id}
            className="group p-3 bg-background border border-border rounded-md hover:border-primary/30 transition-smooth"
          >
            <div className="flex items-start justify-between gap-2 mb-1">
              <button
                onClick={() => loadSearch(search)}
                className="flex-1 text-left"
              >
                <h4 className="text-sm font-medium text-foreground group-hover:text-primary transition-smooth">
                  {search?.name}
                </h4>
              </button>
              <button
                onClick={() => deleteSearch(search?.id)}
                className="p-1 opacity-0 group-hover:opacity-100 hover:bg-destructive/10 rounded transition-smooth"
                aria-label="Delete search"
              >
                <Icon name="Trash2" size={14} className="text-destructive" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              {getFilterSummary(search?.filters)}
            </p>
          </div>
        ))}
      </div>

      {savedSearches?.length > 3 && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-3 text-xs text-primary hover:text-primary/80 transition-smooth flex items-center gap-1"
        >
          {isExpanded ? (
            <>
              <Icon name="ChevronUp" size={14} />
              Show less
            </>
          ) : (
            <>
              <Icon name="ChevronDown" size={14} />
              Show {savedSearches?.length - 3} more
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default SavedSearches;